"use client";

import { Link, useLocation, Outlet } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { cn } from "@/lib/utils";
import { Home, ChevronRight, BuildingOffice, Bell, X } from "@/lib/icons";
import { m, AnimatePresence } from "framer-motion";
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getOrgsWhereUserIsAdmin } from "@/features/org-admin/guard";
import { PendingExportsWidget } from "@/components/analytics/PendingExportsWidget";
import { getCurrentUserProfile } from "@/lib/api";
import { forceWebLogout } from "@/lib/axios";
import { isWebAccessAllowed } from "@/lib/webAccess";

interface AuthenticatedLayoutProps {
  children?: React.ReactNode;
}

const getInitialSidebarOpen = (isOperationsRoute: boolean) =>
  typeof window !== "undefined"
    ? window.matchMedia("(min-width: 1024px)").matches && !isOperationsRoute
    : false;

const getVerifiedAdminState = (
  user: Awaited<ReturnType<typeof getCurrentUserProfile>> | undefined
) => {
  if (!user) return { orgAdminId: null as string | null };
  const orgs = getOrgsWhereUserIsAdmin(user);
  return { orgAdminId: orgs[0] ?? null };
};

export default function AuthenticatedLayout({
  children,
}: AuthenticatedLayoutProps) {
  const { t } = useTranslation(["common", "navigation"]);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    // Desktop scrolls the persistent main pane; mobile scrolls the document.
    // A new page must not inherit the project card's scroll offset.
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
      mainRef.current.scrollLeft = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);
  const isOperationsRoute = /^\/projects\/[^/]+\/operations(?:\/|$)/.test(
    location.pathname
  );
  const isSolutionWorkspaceRoute =
    /^\/projects\/[^/]+\/solutions\/[^/]+(?:\/|$)/.test(location.pathname);
  const isProjectHomeRoute = /^\/projects\/\d+\/?$/.test(location.pathname);
  const isImmersiveProjectRoute =
    isProjectHomeRoute || isOperationsRoute || isSolutionWorkspaceRoute;
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    getInitialSidebarOpen(isImmersiveProjectRoute)
  );
  const { data: verifiedUser } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: getCurrentUserProfile,
    staleTime: 5 * 60_000,
    retry: 1,
    // The realtime `permissions.updated` event evicts the session as soon as
    // web access is revoked; this slow poll is only a safety net.
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (!verifiedUser) return;
    if (isWebAccessAllowed(verifiedUser)) return;
    forceWebLogout();
  }, [verifiedUser]);
  const adminState = useMemo(
    () => getVerifiedAdminState(verifiedUser),
    [verifiedUser]
  );
  const orgAdminId = adminState.orgAdminId;
  const wasImmersiveProjectRouteRef = useRef(isImmersiveProjectRoute);

  useEffect(() => {
    const wasImmersiveProjectRoute = wasImmersiveProjectRouteRef.current;
    wasImmersiveProjectRouteRef.current = isImmersiveProjectRoute;

    if (isImmersiveProjectRoute && !wasImmersiveProjectRoute) {
      setIsSidebarOpen(false);
    }
  }, [isImmersiveProjectRoute]);

  const allSidebarItems = [
    {
      titleKey: "navigation:menu.home",
      href: "/home",
      icon: Home,
    },
    {
      titleKey: "navigation:menu.notifications",
      href: "/notifications",
      icon: Bell,
    },
    ...(orgAdminId
      ? [
          {
            titleKey: "navigation:menu.org_admin_panel",
            href: `/org/${orgAdminId}/admin`,
            icon: BuildingOffice,
          },
        ]
      : []),
  ];

  // Filter out sidebar items when on GIS routes
  const sidebarItems = location.pathname.startsWith("/gis")
    ? []
    : allSidebarItems;

  const toggleSidebar = () => setIsSidebarOpen((isOpen) => !isOpen);

  const sidebarNav = (
    <nav
      aria-label={t("navigation:menu.title", {
        defaultValue: "Main navigation",
      })}
      className="space-y-1 p-4"
    >
      {sidebarItems.map((item) => {
        const Icon = item.icon;
        const isActive =
          location.pathname === item.href ||
          location.pathname.startsWith(`${item.href}/`);
        return (
          <Link
            key={item.href}
            to={item.href}
            onClick={() => {
              if (window.innerWidth < 1024) setIsSidebarOpen(false);
            }}
            className={cn(
              "flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors touch-manipulation",
              "hover:bg-accent hover:text-accent-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "group w-full min-h-10",
              isActive && "bg-accent text-accent-foreground"
            )}
          >
            <Icon className="size-4" />
            <span>
              {typeof item.titleKey === "string" &&
              item.titleKey.startsWith("navigation:")
                ? t(item.titleKey)
                : item.titleKey}
            </span>
            <ChevronRight className="ml-auto size-4 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        );
      })}
    </nav>
  );

  // Account settings owns its navigation and scrolling, including on phones.
  if (/^\/(settings|profile)\/?$/.test(location.pathname)) {
    return (
      <>
        <main
          ref={mainRef}
          id="main-content"
          className="h-dvh min-h-0 overflow-hidden bg-background"
        >
          {children || <Outlet />}
        </main>
        <PendingExportsWidget />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar onToggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />

      <div className="relative flex min-h-[calc(100svh-4rem-env(safe-area-inset-top))] overflow-x-hidden lg:h-[calc(100dvh-4rem-env(safe-area-inset-top))] lg:overflow-hidden">
        {/* Mobile sidebar — full-width slide-over */}
        <AnimatePresence>
          {isSidebarOpen && sidebarItems.length > 0 && (
            <m.aside
              key="sidebar-mobile"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 350 }}
              className="fixed inset-0 z-[45] flex flex-col bg-white pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] dark:bg-zinc-950 lg:hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 h-16 border-b shrink-0">
                <span className="text-lg font-semibold tracking-tight">
                  {t("navigation:menu.title", { defaultValue: "Menú" })}
                </span>
                <button
                  type="button"
                  onClick={() => setIsSidebarOpen(false)}
                  aria-label={t("navigation:actions.close_menu", {
                    defaultValue: "Close menu",
                  })}
                  className="inline-flex size-10 items-center justify-center rounded-lg transition-colors touch-manipulation hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-5" aria-hidden="true" />
                </button>
              </div>

              {/* Nav items */}
              <nav
                aria-label={t("navigation:menu.title", {
                  defaultValue: "Main navigation",
                })}
                className="flex-1 overflow-y-auto px-3 py-4 space-y-1"
              >
                {sidebarItems.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    location.pathname === item.href ||
                    location.pathname.startsWith(`${item.href}/`);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-4 px-4 py-3.5 text-base rounded-xl transition-colors touch-manipulation",
                        "hover:bg-accent hover:text-accent-foreground",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        "w-full",
                        isActive
                          ? "bg-zinc-100 font-medium text-zinc-900 dark:bg-zinc-900 dark:text-zinc-50"
                          : "text-foreground"
                      )}
                    >
                      <Icon className="size-5" />
                      <span>
                        {typeof item.titleKey === "string" &&
                        item.titleKey.startsWith("navigation:")
                          ? t(item.titleKey)
                          : item.titleKey}
                      </span>
                      <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                    </Link>
                  );
                })}
              </nav>
            </m.aside>
          )}
        </AnimatePresence>

        {/* Desktop sidebar changes layout once; animating width here repaints
            every workspace frame, including the MapLibre canvas. */}
        {isSidebarOpen && sidebarItems.length > 0 && (
          <aside
            className={cn(
              "h-full w-[280px] shrink-0 overflow-y-auto border-r bg-white dark:bg-zinc-950",
              "relative hidden lg:block"
            )}
          >
            {sidebarNav}
          </aside>
        )}

        <main
          ref={mainRef}
          id="main-content"
          className="w-full min-w-0 flex-1 overflow-visible pb-[env(safe-area-inset-bottom)] lg:overflow-auto lg:pb-0"
        >
          <div
            className={cn(
              isImmersiveProjectRoute
                ? "w-full max-w-none p-0"
                : "container mx-auto p-3 md:p-6"
            )}
          >
            {children || <Outlet />}
          </div>
        </main>
      </div>
      {/* Global pending-exports widget: revives ExportJobPollers for any
          jobs left in localStorage, so closing the chat panel or navigating
          away doesn't lose the download handle. */}
      <PendingExportsWidget />
    </div>
  );
}
